import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TextOverflow } from '../../../../../../components/TextOverflow';
import { Tooltip } from '../../../../../../components/Tooltip';
import type { NewsSignal as NewsSignalType, SignalType } from '../../../../../../domain/signal';
import { signalService } from '../../../../../../services/signal/signal';
import { useDispatchTrackEventWithLocation } from '../../../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { SignalHeader } from '../../SignalHeader';
import { StyledSignalItem, StyledSignalTagsContainer } from '../../styles';
import { Tag } from '../Tag/Tag';

import { StyledLink, StyledLinkIcon, StyledTitleContainer } from './styles';

type Props = {
  signal: NewsSignalType;
};

export const NewsSignal: React.FC<Props> = ({ signal }) => {
  const { t } = useTranslation();
  const [lastTagMaxWidth, setLastTagMaxWidth] = useState<number | undefined>(undefined);
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const tags = signalService.getNewsSignalEventTags(signal);
  const shouldLimitLastTagWidth = tags.length === 5;

  const lastTagRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !shouldLimitLastTagWidth ||
      !lastTagRef.current ||
      !tagsContainerRef.current ||
      tagsContainerRef.current.scrollWidth <= tagsContainerRef.current.clientWidth
    ) {
      return;
    }

    setLastTagMaxWidth(
      lastTagRef.current.clientWidth - (tagsContainerRef.current.scrollWidth - tagsContainerRef.current.clientWidth)
    );
  }, [shouldLimitLastTagWidth]);

  const handleLinkClick = () => {
    dispatchTrackEventWithLocation('signals', 'read article', `news/${tags.join(',')}/${signal.type}`);
  };

  return (
    <StyledSignalItem>
      <SignalHeader type={signal.type as SignalType} eventHappenedDaysAgo={signal.eventHappenedDaysAgo} />
      <StyledTitleContainer>
        <TextOverflow text={signal.title} />
        <StyledLink href={signal.url} target="_blank" rel="noreferrer" onMouseDown={handleLinkClick}>
          <Tooltip content={t('company.signals.title.url.tooltip')} placement="top" maxWidth={230} minWidth={101}>
            <StyledLinkIcon iconName="link-out" size="xs" />
          </Tooltip>
        </StyledLink>
      </StyledTitleContainer>
      {tags.length ? (
        <StyledSignalTagsContainer ref={tagsContainerRef}>
          {tags.map((tag, index) => {
            const isLastItem = index === tags.length - 1;
            return (
              <Tag
                value={tag}
                key={tag}
                maxWidth={isLastItem && lastTagMaxWidth ? `${lastTagMaxWidth}px` : undefined}
                ref={shouldLimitLastTagWidth && isLastItem ? lastTagRef : undefined}
              />
            );
          })}
        </StyledSignalTagsContainer>
      ) : null}
    </StyledSignalItem>
  );
};
